<template>
  <AIVDialog
    v-model="isVisible"
    title="Shift History"
    width="90%"
    height="85vh"
    class="shift-history-modal"
    :footer="false"
  >
    <div class="shift-history-container">
      <div v-if="shift" class="shift-summary mb-4">
        <div class="summary-header">
          <h5>Shift Details</h5>
        </div>
        <div class="summary-content">
          <div class="summary-row">
            <span class="summary-label">Date:</span>
            <span class="summary-value">{{ formatDate(getActualShiftDate(shift)) }}</span>
          </div>
          <div
            v-if="
              shift.clock_in ||
              shift.clock_out ||
              shift.clockIn ||
              shift.clockOut
            "
            class="summary-row"
          >
            <span class="summary-label">Actual Time:</span>
            <span class="summary-value">
              {{ formatActualTimeRange(shift) }}
            </span>
          </div>
          <div v-if="clockInGeocode" class="summary-row">
            <span class="summary-label">Clock In Location:</span>
            <span class="summary-value">
              {{ clockLocationLabel(clockInGeocode) }}
              <a
                :href="clockMapUrl(clockInGeocode)"
                target="_blank"
                rel="noopener"
                class="clock-map-link"
              ><i class="fa-solid fa-map-pin"></i> Map</a>
            </span>
          </div>
          <div v-if="clockOutGeocode" class="summary-row">
            <span class="summary-label">Clock Out Location:</span>
            <span class="summary-value">
              {{ clockLocationLabel(clockOutGeocode) }}
              <a
                :href="clockMapUrl(clockOutGeocode)"
                target="_blank"
                rel="noopener"
                class="clock-map-link"
              ><i class="fa-solid fa-map-pin"></i> Map</a>
            </span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Scheduled Time:</span>
            <span class="summary-value">
              {{
                formatTimeRange(
                  shift.scheduled_start || shift.startTime,
                  shift.scheduled_end || shift.endTime,
                )
              }}
            </span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Client:</span>
            <span class="summary-value">{{ getClientName(shift) }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Caregiver:</span>
            <span class="summary-value">{{ getCaregiverName(shift) }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Status:</span>
            <span class="summary-value">
              <span
                :class="[
                  'status-badge',
                  getStatusClass(shift.shift_status || shift.status),
                ]"
              >
                {{ formatStatus(shift.shift_status || shift.status) }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <!-- Per-caregiver clock-in/out locations (group shifts) -->
      <div v-if="caregiverClockLocations.length" class="caregiver-locations-section">
        <h5>Clock Locations</h5>
        <div
          v-for="loc in caregiverClockLocations"
          :key="'loc-' + loc.id"
          class="caregiver-location-block"
        >
          <div class="caregiver-location-name">{{ loc.name }}</div>
          <div v-if="loc.inGeocode" class="summary-row">
            <span class="summary-label">Clock In Location:</span>
            <span class="summary-value">
              {{ clockLocationLabel(loc.inGeocode) }}
              <a
                :href="clockMapUrl(loc.inGeocode)"
                target="_blank"
                rel="noopener"
                class="clock-map-link"
              ><i class="fa-solid fa-map-pin"></i> Map</a>
            </span>
          </div>
          <div v-if="loc.outGeocode" class="summary-row">
            <span class="summary-label">Clock Out Location:</span>
            <span class="summary-value">
              {{ clockLocationLabel(loc.outGeocode) }}
              <a
                :href="clockMapUrl(loc.outGeocode)"
                target="_blank"
                rel="noopener"
                class="clock-map-link"
              ><i class="fa-solid fa-map-pin"></i> Map</a>
            </span>
          </div>
        </div>
      </div>

      <div class="history-section">
        <div class="history-header">
          <h5>History Timeline</h5>
          <span v-if="history && history.length" class="history-count">
            {{ history.length }}
            {{ history.length === 1 ? "entry" : "entries" }}
          </span>
        </div>

        <div v-if="loading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <p class="mt-2">Loading history...</p>
        </div>

        <div v-else-if="!history || history.length === 0" class="no-history">
          <i class="fas fa-info-circle fa-2x text-muted"></i>
          <p class="mt-2 text-muted">
            No history records found for this shift.
          </p>
        </div>

        <div v-else class="history-timeline">
          <div v-for="item in history" :key="item.id" class="history-item">
            <div class="history-marker">
              <i class="fas fa-circle"></i>
            </div>
            <div class="history-content">
              <div class="history-meta">
                <span class="history-date">
                  {{ formatDateTime(item.timestamp) }}
                </span>
                <span v-if="item.user" class="history-user">
                  by {{ item.user.name }} ({{ item.user.type }})
                </span>
              </div>
              <div class="history-changes">{{ item.changes }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <el-button @click="closeModal">Close</el-button>
      </div>
    </div>
  </AIVDialog>
</template>

<script>
import clockLocationMixin from "@/mixins/clockLocationMixin";

export default {
  name: "ShiftHistoryModal",
  mixins: [clockLocationMixin],
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    shift: {
      type: Object,
      default: null,
    },
    history: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    instanceDate: {
      type: String,
      default: null,
    },
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },

    // Captured clock-in/out coordinates for the shift ("lat,lng" strings).
    // These are the 1-on-1 (shift-level) coordinates; group shifts store
    // coordinates per caregiver instead (see caregiverClockLocations).
    clockInGeocode() {
      return this.shift?.clock_in_geocode || null;
    },

    clockOutGeocode() {
      return this.shift?.clock_out_geocode || null;
    },

    // Per-caregiver clock-in/out coordinates for group shifts. Only includes
    // caregivers that actually have at least one captured location.
    caregiverClockLocations() {
      const clockTimes = this.shift?.caregiverClockTimes || [];
      return clockTimes
        .filter((ct) => ct.clock_in_geocode || ct.clock_out_geocode)
        .map((ct) => ({
          id: ct.id_users,
          name:
            `${ct.caregiver?.first_name || ""} ${ct.caregiver?.last_name || ""}`.trim() ||
            "Caregiver",
          inGeocode: ct.clock_in_geocode || null,
          outGeocode: ct.clock_out_geocode || null,
        }));
    },
  },
  watch: {
    shift: {
      immediate: true,
      handler() {
        const geocodes = [this.clockInGeocode, this.clockOutGeocode];
        for (const loc of this.caregiverClockLocations) {
          geocodes.push(loc.inGeocode, loc.outGeocode);
        }
        this.resolveClockLocations(geocodes);
      },
    },
  },
  methods: {
    closeModal() {
      this.isVisible = false;
    },
    formatDate(date) {
      if (!date) return "N/A";


      let d;
      // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        // For date-only strings, create date in local timezone to avoid UTC midnight issues
        const [year, month, day] = date.split('-');
        d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        d = new Date(date);
      }

      const result = d.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      return result;
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";
      const d = new Date(dateTime);
      return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    formatTimeRange(start, end) {
      if (!start || !end) return "N/A";

      const isUserInDenverTime = this.isUserInDenverTimezone();

      const formatTime = (dateTimeStr) => {
        if (!dateTimeStr) return "";

        // Handle full datetime strings (ISO format)
        let date = new Date(dateTimeStr);
        if (!isNaN(date.getTime())) {
          // Only convert timezone if user is not in Denver time
          const timeZone = isUserInDenverTime ? "America/Denver" : undefined;

          return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZone: timeZone,
          });
        }

        // Fallback for time-only strings
        const [hours, minutes] = dateTimeStr.split(":");
        const hour = parseInt(hours);
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${displayHour}:${minutes} ${period}`;
      };

      return `${formatTime(start)} - ${formatTime(end)}`;
    },
    convertUTCTimeToLocalTime(timeStr) {
      if (!timeStr || !this.shift?.date) return timeStr;

      try {
        const shiftDate = this.shift.date.split("T")[0];
        const utcDateTime = `${shiftDate}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (isNaN(utcDate.getTime())) {
          return timeStr;
        }

        return utcDate.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return timeStr;
      }
    },
    getClientName(shift) {
      if (!shift) return "N/A";

      // Handle array of clients
      if (
        shift.clients &&
        Array.isArray(shift.clients) &&
        shift.clients.length > 0
      ) {
        return shift.clients
          .map(
            (c) =>
              `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
              "Unknown Client",
          )
          .join(", ");
      }

      // Handle single client object
      if (shift.client) {
        return (
          `${shift.client.first_name || ""} ${shift.client.last_name || ""}`.trim() ||
          "Unknown Client"
        );
      }

      // Handle client name fields directly on shift
      if (shift.client_name) {
        return shift.client_name;
      }

      if (shift.client_first_name || shift.client_last_name) {
        return (
          `${shift.client_first_name || ""} ${shift.client_last_name || ""}`.trim() ||
          "Unknown Client"
        );
      }

      return "N/A";
    },
    getCaregiverName(shift) {
      if (!shift) return "N/A";

      // Handle array of caregivers
      if (
        shift.caregivers &&
        Array.isArray(shift.caregivers) &&
        shift.caregivers.length > 0
      ) {
        return shift.caregivers
          .map(
            (c) =>
              `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
              "Unknown Caregiver",
          )
          .join(", ");
      }

      // Handle single caregiver object
      if (shift.caregiver) {
        return (
          `${shift.caregiver.first_name || ""} ${shift.caregiver.last_name || ""}`.trim() ||
          "Unknown Caregiver"
        );
      }

      // Handle caregiver name fields directly on shift
      if (shift.caregiver_name) {
        return shift.caregiver_name;
      }

      if (shift.caregiver_first_name || shift.caregiver_last_name) {
        return (
          `${shift.caregiver_first_name || ""} ${shift.caregiver_last_name || ""}`.trim() ||
          "Unknown Caregiver"
        );
      }

      return "Unassigned";
    },
    getActualShiftDate(shift) {

      // Use clock in time if available, otherwise use scheduled date
      const clockIn = shift.clock_in || shift.clockIn;
      if (clockIn) {
        return clockIn;
      }

      // Use instanceDate prop if provided (for recurring shifts)
      if (this.instanceDate) {
        return this.instanceDate;
      }

      // For recurring shifts, extract date from scheduled_start
      if (shift.scheduled_start) {
        return shift.scheduled_start;
      }

      return shift.date;
    },
    formatStatus(status) {
      if (!status) return "Scheduled";

      const statusMap = {
        "scheduled": "Scheduled",
        "unassigned": "Unassigned",
        "clocked-in": "Clocked In",
        "clocked in": "Clocked In",
        "clocked-out": "Clocked Out",
        "clocked out": "Clocked Out",
        "approved": "Approved",
        "missed": "Missed",
        "canceled": "Canceled",
        "cancelled": "Canceled"
      };

      return statusMap[status.toLowerCase()] || status;
    },
    getStatusClass(status) {
      if (!status) return "status-scheduled";

      const statusLower = status.toLowerCase();

      if (statusLower === "approved") return "status-approved";
      if (statusLower === "clocked-in" || statusLower === "clocked in")
        return "status-clocked-in";
      if (statusLower === "clocked-out" || statusLower === "clocked out")
        return "status-clocked-out";
      if (statusLower === "scheduled") return "status-scheduled";
      if (statusLower === "unassigned") return "status-unassigned";
      if (statusLower === "missed") return "status-missed";
      if (statusLower === "canceled" || statusLower === "cancelled")
        return "status-canceled";

      return "status-default";
    },
    formatClockTime(dateTime) {
      if (!dateTime) return "N/A";
      const date = new Date(dateTime);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
    formatClockDate(dateTime) {
      if (!dateTime) return "";
      const date = new Date(dateTime);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
    isUserInDenverTimezone() {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return userTimezone === "America/Denver" || userTimezone === "America/Mountain";
    },
    formatActualTimeRange(shift) {
      // Handle both snake_case and camelCase formats
      const clockIn = shift.clock_in || shift.clockIn;
      const clockOut = shift.clock_out || shift.clockOut;

      if (!clockIn && !clockOut) {
        return "Not clocked in";
      }

      const clockInTime = clockIn ? this.formatClockTime(clockIn) : "";
      const clockOutTime = clockOut ? this.formatClockTime(clockOut) : "";

      // Remove date suffix from actual time display
      let clockOutDateStr = "";

      if (clockIn && clockOut) {
        return `${clockInTime} - ${clockOutTime}${clockOutDateStr}`;
      } else if (clockIn) {
        return `${clockInTime} - In Progress`;
      } else {
        return "Not clocked in";
      }
    },
  },
};
</script>

<style scoped>
.shift-history-container {
  max-width: 800px;
  min-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.shift-summary {
  margin-bottom: 2rem;
}

.summary-header h5,
.history-header h5,
.caregiver-locations-section h5 {
  color: #fff;
  margin: 0 0 1rem 0;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.caregiver-locations-section {
  margin-bottom: 2rem;
}

.caregiver-location-block {
  margin-bottom: 1rem;
}

.caregiver-location-name {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.summary-label {
  font-weight: 600;
  color: #fff;
  width: 140px;
}

.summary-value {
  color: #e0e0e0;
}

.clock-map-link {
  color: #6ba6cd;
  margin-left: 0.35rem;
  white-space: nowrap;
}

.clock-map-link:hover {
  text-decoration: underline;
}

/* Override status badge styling to match Shifts page */
.shift-history-container .status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Status colors to match Shifts page */
.shift-history-container .status-unassigned {
  background: linear-gradient(135deg, rgba(32, 201, 151, 0.7), rgba(32, 201, 151, 0.5));
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.shift-history-container .status-scheduled {
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.8), rgba(13, 110, 253, 0.6));
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.shift-history-container .status-clocked-in {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 193, 7, 0.7));
  color: #000;
  border: 1px solid rgba(0, 0, 0, 0.2);
}

.shift-history-container .status-clocked-out {
  background: linear-gradient(135deg, rgba(237, 137, 54, 0.7), rgba(237, 137, 54, 0.5));
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.shift-history-container .status-approved {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.7), rgba(156, 39, 176, 0.5));
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.shift-history-container .status-missed {
  background: linear-gradient(135deg, rgba(108, 117, 125, 0.8), rgba(108, 117, 125, 0.6));
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.shift-history-container .status-canceled {
  /* Canceled - Dark brown-black, matches Shifts page .badge-canceled */
  background: linear-gradient(135deg, rgba(75, 50, 30, 0.9), rgba(75, 50, 30, 0.7));
  color: #fff;
  border: 1px solid rgba(75, 50, 30, 0.3);
}

.shift-history-container .status-default {
  background: linear-gradient(135deg, rgba(108, 117, 125, 0.6), rgba(108, 117, 125, 0.4));
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.history-section {
  margin-top: 2rem;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.history-count {
  color: #6ba6cd;
  font-size: 0.9rem;
}

.no-history {
  text-align: center;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
}

.no-history i {
  color: #6ba6cd;
}

.no-history p {
  color: #e0e0e0;
  margin-top: 1rem;
}

.history-timeline {
  position: relative;
  padding-left: 30px;
}

.history-timeline::before {
  content: "";
  position: absolute;
  left: 10px;
  top: 10px;
  bottom: 10px;
  width: 2px;
  background: rgba(107, 166, 205, 0.3);
}

.history-item {
  position: relative;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
}

.history-marker {
  position: absolute;
  left: -24px;
  top: 5px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2c3e50;
}

.history-marker i {
  color: #6ba6cd;
  font-size: 10px;
}

.history-item:first-child .history-marker i {
  color: #28a745;
  font-size: 12px;
}

.history-content {
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
}

.history-meta {
  display: flex;
  gap: 10px;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
}

.history-date {
  color: #6ba6cd;
  font-weight: 500;
}

.history-user {
  color: #9ca3af;
}

.history-changes {
  color: #e0e0e0;
  white-space: pre-wrap;
  line-height: 1.6;
  font-size: 0.95rem;
}

.modal-footer {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
}
</style>
